import { getFragmentData } from 'gql'
import {
  ErrorFragmentFragmentDoc,
  WorkspaceFragmentForGeneralSetupGeneralFragmentDoc,
} from 'gql/graphql'
import { useSnackbar } from 'notistack'
import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import { GeneralPageContext } from 'features/globalSetup/contexts/GeneralPageContext'
import { updateUser, useAuthUser } from 'features/user'
import { useAbility } from 'providers/casl'
import { mapGraphQLErrors } from 'providers/tanstack-query/errors'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { toDTO } from './api/mapper'
import { UPDATE_WORKSPACE_FOR_GLOBAL_SETUP_GENERAL } from './api/mutation'

import type { Schema } from '../../schema'
import type { UseUpdateWorkspace } from './types'
import type { WorkspaceSlice } from 'features/globalSetup/contexts/GeneralPageContext/types'
import type {
  UpdateWorkspaceForGlobalSetupGeneralMutation,
  UpdateWorkspaceForGlobalSetupGeneralMutationVariables,
  WorkspaceFragmentForGeneralSetupGeneralFragment,
} from 'gql/graphql'
import type { FieldErrors } from 'react-hook-form'

export const useUpdateWorkspace = (): UseUpdateWorkspace => {
  const ability = useAbility()
  const { t } = useTranslation('features/globalSetup')
  const {
    user: { currentWorkspace },
  } = useAuthUser()
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const { setLoading, setWorkspace } = useContext(GeneralPageContext)
  const [errors, setErrors] = useState<FieldErrors<Schema>>({})

  const updateUserStore = (workspace: WorkspaceFragmentForGeneralSetupGeneralFragment): void => {
    if (!currentWorkspace) {
      throw Error('User workspace should exists')
    }

    void dispatch(
      updateUser({
        currentWorkspace: {
          ...currentWorkspace,
          ...workspace,
        },
      }),
    )
  }

  const { isPending, mutateAsync } = useMutation<
    UpdateWorkspaceForGlobalSetupGeneralMutationVariables,
    UpdateWorkspaceForGlobalSetupGeneralMutation
  >({
    mutation: UPDATE_WORKSPACE_FOR_GLOBAL_SETUP_GENERAL,
    onCompleted: async ({
      updateWorkspace,
    }: UpdateWorkspaceForGlobalSetupGeneralMutation): Promise<void> => {
      const errors = getFragmentData(ErrorFragmentFragmentDoc, updateWorkspace?.errors)
      const workspace = getFragmentData(
        WorkspaceFragmentForGeneralSetupGeneralFragmentDoc,
        updateWorkspace?.workspace,
      )

      if (errors && errors.length > 0) {
        setErrors(mapGraphQLErrors(errors))
        enqueueSnackbar(t('general.workspace.update.error'), { variant: 'error' })
      } else if (workspace) {
        setErrors({})
        enqueueSnackbar(t('general.workspace.update.success'), { variant: 'success' })

        setWorkspace((prev: WorkspaceSlice): WorkspaceSlice => ({ ...prev, ...workspace }))
        updateUserStore(workspace)
      }
    },
    queriesToInvalidate: ['getDomainAmazons', 'getAdUnitAmazons'],
  })

  useEffect(() => {
    setLoading(isPending)
  }, [isPending])

  const update = (data: Schema): void => {
    mutateAsync({
      canReadPriceGenius: ability.can('read', 'GlobalSetupField-priceGenius'),
      input: toDTO(data, ability),
    })
  }

  return { errors, update }
}
